import { Component, OnInit, OnDestroy } from '@angular/core';
import { Partner, PartnerType } from '../../models/partner';
import { PaginatedCollection } from '../../models/PaginatedCollection';
import { PartnersService } from '../../../services/partners.service';
import { Subject, of, debounceTime, takeUntil, switchMap, tap, catchError } from 'rxjs';

@Component({
  selector: 'app-partners',
  templateUrl: './partners.component.html',
  styleUrls: ['./partners.component.scss']
})
export class PartnersComponent implements OnInit, OnDestroy {
  partners: Partner[] = [];
  error: string | null = null;
  selectedType: string = '';
  partnerTypes = Object.values(PartnerType);

  // A string, not a boolean: <option [value]="true"> comes back through ngModel as the string
  // 'true' anyway, so a boolean field here would be a type lie that breaks the isTenantParam
  // check below and turns "Non-tenants" into "All".
  tenantFilter: '' | 'true' | 'false' = '';

  // Two separate flags on purpose. The full-page "Loading partners..." block is only for the
  // very first fetch; every later one keeps the table on screen and just dims it, so the page
  // does not jump and the search box is never unmounted from under the caret.
  initialLoading = true;
  refreshing = false;

  // Search
  searchTerm: string = '';

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  /** Fed only by (input) on the search box. Every other trigger goes straight to reload$. */
  private searchInput$ = new Subject<string>();
  private reload$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  /**
   * What the last fetch actually asked for. This replaces distinctUntilChanged, which kept its
   * copy inside the operator where clearSearch()/refreshPartners() could not reach it — so after
   * clearing the box and retyping the same term, the operator swallowed it and nothing refreshed.
   */
  private lastFetchedTerm = '';

  constructor(private partnersService: PartnersService) {}

  ngOnInit(): void {
    this.searchInput$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(term => {
      if ((term ?? '').trim() === this.lastFetchedTerm) {
        return;
      }
      this.currentPage = 0;
      this.reload$.next();
    });

    this.reload$.pipe(
      tap(() => {
        this.refreshing = true;
        this.error = null;
      }),
      switchMap(() => {
        const searchTerm = this.searchTerm?.trim() ?? '';
        this.lastFetchedTerm = searchTerm;

        return this.partnersService.getPartners({
          type: this.selectedType,
          page: this.currentPage,
          size: this.pageSize,
          sort: ['contact.name,asc'],
          searchTerm: searchTerm || undefined,
          isTenant: this.isTenantParam
        }).pipe(
          // Inside the switchMap projection deliberately: on the outer pipe the first HTTP
          // failure would complete the stream, and the page would stop reacting to typing
          // for the rest of its life.
          catchError(error => {
            console.error('Error loading partners:', error);
            this.error = 'Failed to load partners. Please try again.';
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => this.applyResponse(response));

    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyResponse(response: PaginatedCollection<Partner> | null): void {
    // null means the request failed — leave whatever rows are on screen alone rather than
    // blanking the table underneath the error banner.
    if (response) {
      const data = Array.isArray(response.data) ? response.data : [];
      this.partners = data;
      this.totalElements = response.total || 0;
      this.totalPages = Math.ceil(this.totalElements / this.pageSize);
    }

    this.initialLoading = false;
    this.refreshing = false;
  }

  /** Bypasses the search-term guard so a manual refresh always refetches. */
  refreshPartners(): void {
    this.currentPage = 0;
    this.searchTerm = '';
    this.reload$.next();
  }

  private get isTenantParam(): boolean | undefined {
    return this.tenantFilter === '' ? undefined : this.tenantFilter === 'true';
  }

  onTypeFilterChange(): void {
    this.currentPage = 0;
    this.reload$.next();
  }

  onTenantFilterChange(): void {
    this.currentPage = 0;
    this.reload$.next();
  }

  onSearchTermChange(): void {
    this.searchInput$.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 0;
    this.reload$.next();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.reload$.next();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = parseInt(target.value, 10);
    this.currentPage = 0;
    this.reload$.next();
  }

  trackByPartnerId(index: number, partner: Partner): number {
    return partner.id ?? index;
  }


  getFormattedAddress(partner: Partner): string {
    if (!partner.contact?.address) {
      return '-';
    }
    const addr = partner.contact.address;
    return `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zip || ''}`.trim();
  }
}
